// TODO: Lodash should no longer be used here
// eslint-disable-next-line no-restricted-imports
import { kebabCase } from 'lodash';
import { Route, Routes } from 'react-router-dom';

import { MyRolesPage } from '../../Pages/MyRoles/MyRolesPage';
import { useRoutes } from '../navigationHooks';
import { paths } from '../paths';
import { DefaultRoute, GuestRoute, VerifyRoute } from './routes';

// Transition Pages
import AuthReturnPage from '../../Pages/AuthReturn';
import CompleteSignupPage from '../../Pages/CompleteSignup';
import SignoutPage from '../../Pages/Signout';

// Guest Pages
import SigninPage from '../../Pages/Signin';
import SignUpPage from '../../Pages/Signup';

// User Pages
import EmailSentPage from '../../Pages/EmailSent';
import MarketplaceExternal from '../../Pages/MarketplaceExternal';

import ErrorPage from '../../Pages/Error';
import UnauthorizedPage from '../../Pages/Unauthorized';
import { FeatureFlagsPage, useFeatureFlags } from '../../feature-flags';

import { useIntercomUpdate } from '../../hooks/useIntercomUpdate';
import useOrganizationImpersonationResetListener from '../../hooks/useOrganizationImpersonationResetListener';
import { useOrganization } from '../../hooks/useOrganizations';
import useTrackers from '../../hooks/useTrackers';
import { useMarketplaceHeadless } from '../../integrations';
import { buildMarketplaceLink } from '../navigationUtils';
import { RestrictedRoute } from './routes/RestrictedRoute';

const AppRouter = () => {
  useOrganizationImpersonationResetListener();
  useTrackers();
  useIntercomUpdate();

  const { FeatureFlagsUIFlag } = useFeatureFlags();
  const { allRoutes } = useRoutes();
  const organization = useOrganization();
  const headlessConfig = useMarketplaceHeadless();

  return (
    <Routes>
      {/* Transition Routes */}
      <Route path={paths.authReturn} element={<AuthReturnPage />} />
      <Route path={paths.signout} element={<SignoutPage />} />
      <Route path={paths.signoutFull} element={<SignoutPage fullSignoutMode={true} />} />

      <Route
        path={paths.completeSignup}
        element={
          <RestrictedRoute route={{ path: paths.completeSignup }}>
            <CompleteSignupPage />
          </RestrictedRoute>
        }
      />

      {/* Guest Routes */}
      <Route
        path={paths.signin}
        element={
          <GuestRoute redirect={paths.home}>
            <SigninPage />
          </GuestRoute>
        }
      />
      <Route
        path={paths.signup}
        element={
          <GuestRoute redirect={paths.home}>
            <SignUpPage />
          </GuestRoute>
        }
      />

      {/* User Routes */}
      {createRestrictedRoutes(allRoutes, organization, headlessConfig)}

      <Route
        path={paths.verify}
        element={
          <VerifyRoute>
            <EmailSentPage />
          </VerifyRoute>
        }
      />

      {/* My Roles — multi-role accounts settings page. Guarded like every other user route, so a
          signed-out visitor gets the sign-in page instead of the CCMS shell around a load error.
          RestrictedRoute exempts this path from the parent-context eject: it is the way back. */}
      <Route
        path={paths.myRoles}
        element={
          <RestrictedRoute route={{ path: paths.myRoles }}>
            <MyRolesPage />
          </RestrictedRoute>
        }
      />

      {/* Misc Routes */}
      <Route path={paths.r401} element={<UnauthorizedPage />} />
      <Route path={paths.r404} element={<ErrorPage />} />
      <Route path={paths.authError} element={<ErrorPage />} />
      {FeatureFlagsUIFlag.enabled && <Route path={paths.featureFlags} element={<FeatureFlagsPage />} />}
      <Route path={paths.error} element={<ErrorPage />} />
      <Route path="*" element={<DefaultRoute component={paths.home} />} />
    </Routes>
  );
};

function createRestrictedRoutes(routes, organization, headlessConfig) {
  const makeRestrictedRoute = (route, headlessConfig) => {
    const Component = route.component;
    const isAppRoute = !!route?.path && !!route.component;
    const isHeadlessRoute = !!route.headlessPath;
    const channelId = headlessConfig.channelId;

    if (isAppRoute) {
      return (
        <Route
          key={route.path}
          path={route.path}
          element={
            <RestrictedRoute route={route}>
              <Component />
            </RestrictedRoute>
          }
        />
      );
    } else if (isHeadlessRoute) {
      const pathSlug = `/${kebabCase(route.name)}`;
      const url = buildMarketplaceLink({ path: route.headlessPath, organization, channelId });
      return (
        <Route
          key={pathSlug}
          path={pathSlug}
          element={
            <RestrictedRoute route={route}>
              <MarketplaceExternal url={url} routeName={route.name} />
            </RestrictedRoute>
          }
        />
      );
    }

    return null;
  };

  return routes.map((route) => makeRestrictedRoute(route, headlessConfig));
}
export default AppRouter;
